import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import React from 'react';
import { toast } from 'react-hot-toast';

import { ContactAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Button, ButtonLink } from '@poseidon/components/Input2';
import { Popup } from '@poseidon/components/Popup';
import { Alerts, Users } from '@poseidon/components/StrokeIcons';

import { api } from '@/utils/api';
import { contactDetailHref } from '~/components/Todo/UpcomingCallsStrip';

/**
 * Everyone a bulk add refused, by refusal category (POS-2482). Every category is
 * optional; the popup renders a section per non-empty one, so categories can co-occur
 * (e.g. the onlyNewPeople pre-filter alongside an in-sequence refusal).
 */
export interface SkippedContactsCategories {
  /** onlyNewPeople pre-filter: previously added to Take Action. The only category with an "Add these anyway" escape hatch. */
  previouslyAdded?: string[];
  /**
   * POS-2453 refusals: a live sequence holds the contact — named and linked. Deliberately NO
   * "Add anyway": one outreach mechanism at a time is the invariant, not a preference.
   */
  inSequence?: { contactPublicId: string; sequenceName: string; sequencePublicId: string }[];
  /** POS-2455 refusals: every outreach step in the campaign was already sent to them. */
  alreadySent?: string[];
  /** POS-2407 refusals: a prior audience-filter reject permanently bars the re-add. */
  previouslyRejected?: string[];
}

interface Props {
  teamId: string;
  skipped: SkippedContactsCategories;
  onClose: () => void;
  /**
   * Called when the user clicks "Add these anyway" on the previously-added
   * section. Receives that section's public ids. Implementation is expected to
   * re-fire the original add mutation with `onlyNewPeople: false` against just
   * these contacts. The button renders only when this is provided AND the
   * previously-added category is non-empty.
   */
  onAddAnyway?: (contactPublicIds: string[]) => Promise<unknown>;
}

const CATEGORY_COPY = {
  previouslyAdded: {
    title: 'Previously added',
    guidance:
      'You’ve added these contacts to take action before. They were skipped because "Only new people" is selected.',
  },
  inSequence: {
    title: 'Already in a sequence',
    guidance:
      'A contact is worked by one outreach mechanism at a time — remove them from their sequence first if this campaign should take over.',
  },
  alreadySent: {
    title: 'Everything already sent',
    guidance:
      'Every outreach step in this campaign already went to them. To force a fresh run, flip the Resume/Fresh control and add them again.',
  },
  previouslyRejected: {
    title: 'Previously rejected by the audience filter',
    guidance: 'This campaign’s audience filter rejected them before. Use Re-vet on its staging list to reconsider.',
  },
} as const;

/**
 * The durable record of a bulk add's refusals (POS-2482): the one-line toast is only the
 * attention-getter; this popup persists until dismissed and says WHO was skipped, WHY, and what to
 * do about it, per category.
 */
export function SkippedContactsPopup({ teamId, skipped, onClose, onAddAnyway }: Props) {
  const previouslyAdded = skipped.previouslyAdded ?? [];
  const inSequence = skipped.inSequence ?? [];
  const alreadySent = skipped.alreadySent ?? [];
  const previouslyRejected = skipped.previouslyRejected ?? [];

  const allContactPublicIds = [
    ...previouslyAdded,
    ...inSequence.map((e) => e.contactPublicId),
    ...alreadySent,
    ...previouslyRejected,
  ];
  const displayQuery = api.contact.displayInfoByPublicIds.useQuery(
    { teamId, contactPublicIds: allContactPublicIds },
    { enabled: allContactPublicIds.length > 0 },
  );
  const contactByPublicId = new Map((displayQuery.data ?? []).map((c) => [c.contactPublicId, c]));

  const contactRow = (contactPublicId: string, aside?: React.ReactNode) => {
    const c = contactByPublicId.get(contactPublicId);
    const name =
      c != null
        ? [c.firstName, c.lastName].filter((v) => v != null && v !== '').join(' ') || contactPublicId
        : contactPublicId;
    return (
      <div key={contactPublicId} className="flex items-center gap-2 px-2 py-1">
        <ContactAvatar
          firstName={c?.firstName}
          lastName={c?.lastName}
          imageSrc={c?.imageSrc}
          shape="circle"
          className="size-7"
        />
        <ButtonLink text href={contactDetailHref(contactPublicId)} className="text-sm">
          {name}
        </ButtonLink>
        {aside}
      </div>
    );
  };

  const section = (kind: keyof typeof CATEGORY_COPY, count: number, rows: React.ReactNode) => {
    if (count === 0) return null;
    return (
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
          {CATEGORY_COPY[kind].title} ({count})
        </h3>
        <p className="text-xs text-neutral-600 dark:text-gray-400">{CATEGORY_COPY[kind].guidance}</p>
        <div
          className={cn(
            'flex flex-col gap-1 max-h-48 overflow-y-auto rounded-sm border',
            'border-neutral-200 dark:border-gray-700 p-1',
          )}>
          {rows}
        </div>
      </div>
    );
  };

  const totalSkipped = allContactPublicIds.length;

  const handleAddAnyway = () => {
    if (onAddAnyway == null) return;
    return updateWithToast({
      loading: 'Adding...',
      success: `Added ${previouslyAdded.length}`,
      mutationFnAsync: async () => {
        await onAddAnyway(previouslyAdded);
        onClose();
      },
    });
  };

  const handleKeepSkipped = () => {
    toast.success(`Kept ${previouslyAdded.length} skipped`);
    onClose();
  };

  return (
    <Popup onClose={onClose}>
      <div className="flex flex-col gap-3 text-left max-w-lg w-full">
        <div className="flex items-center gap-2 text-neutral-900 dark:text-white">
          <Alerts.AlertCircle className="size-5" />
          <h2 className="text-lg font-bold">
            Skipped {totalSkipped} {totalSkipped === 1 ? 'contact' : 'contacts'}
          </h2>
        </div>
        {section(
          'previouslyAdded',
          previouslyAdded.length,
          previouslyAdded.map((id) => contactRow(id)),
        )}
        {section(
          'inSequence',
          inSequence.length,
          inSequence.map((entry) =>
            contactRow(
              entry.contactPublicId,
              <span className="text-xs text-neutral-500 dark:text-gray-400 min-w-0 flex items-baseline gap-1">
                in
                <ButtonLink text href={`/sequence/${entry.sequencePublicId}`} className="text-xs">
                  {entry.sequenceName}
                </ButtonLink>
              </span>,
            ),
          ),
        )}
        {section(
          'alreadySent',
          alreadySent.length,
          alreadySent.map((id) => contactRow(id)),
        )}
        {section(
          'previouslyRejected',
          previouslyRejected.length,
          previouslyRejected.map((id) => contactRow(id)),
        )}
        {allContactPublicIds.length > 0 && contactByPublicId.size === 0 && displayQuery.isLoading && (
          <div className="px-2 py-1 text-sm text-neutral-500">Loading...</div>
        )}
        <div className="flex justify-end gap-2">
          {onAddAnyway != null && previouslyAdded.length > 0 && (
            <>
              <Button onClick={handleKeepSkipped} color="red" tone="outline" icon={<Users.UserPlus01 />}>
                Keep skipped
              </Button>
              <Button onClick={handleAddAnyway} color="blue" icon={<Users.Users01 />}>
                Add these anyway
              </Button>
            </>
          )}
          {(onAddAnyway == null || previouslyAdded.length === 0) && (
            <Button onClick={onClose} color="blue">
              Got it
            </Button>
          )}
        </div>
      </div>
    </Popup>
  );
}
